<template>
    <div
        class="relative"
        @mouseenter="hoverActive = true"
        @mouseleave="hoverActive = false"
        v-bind="{ class: attrs.class }"
    >
        <!-- Toggle Button -->
        <edit-toggle-button
            v-show="portalContentAndDesignStore.liveDesignerEnabled && (isEditing || hoverActive)"
            :is-editing="isEditing"
            @toggle="toggleEdit"
            class="transition-opacity duration-200"
        />

        <!-- Normal Display Mode -->
        <btn
            v-if="!isEditing"
            :type="type"
            :size="size"
            :to="to"
            :href="href"
            :disabled="disabled"
            :icon-name="iconName"
            :icon-right="iconRight"
            :rounded-full="roundedFull"
            :corners="corners"
            :loading="loading"
            :prevent-double-click="preventDoubleClick"
            :button-type="buttonType"
            :class="[customClass, portalContentAndDesignStore.getPageContentButtonTextColorClass]"
            :link-target="linkTarget"
            v-bind="{ class: attrs.class }"
        >
            <slot>{{ model }}</slot>
        </btn>

        <!-- Editable Input Mode -->
        <input
            v-else-if="isEditing"
            ref="inputEl"
            v-model="model"
            type="text"
            :class="[
                'focus:ring-black border border-gray-300 w-full text-center',
                [types[type] ? types[type].style : ''],
                [sizing[size].size],
                customClass,
                roundedFull ? 'rounded-full' : (corners || 'rounded'),
                type === 'link' ? '' : hasSlotData ? [sizing[size].slot] : [sizing[size].noSlot],
            ]"
            :name="name"
            v-bind="{ class: attrs.class }"
        />
    </div>
</template>

<script setup>
import {ref, computed, useAttrs, useSlots} from 'vue';
import {usePortalContentAndDesignStore} from "../../../stores/partners/portalContentAndDesignStore";
import EditToggleButton from "../Pub/components/EditToggleButton.vue";
import {useEditableFocus} from "../../../composables/partners/useEditableFocus";



const props = defineProps({
    modelValue: String,
    name: String,
    type: {
        type: String,
        default: 'primary'
    },
    size: {
        type: String,
        default: 'default'
    },
    to: [String, Object],
    href: String,
    iconName: String,
    iconRight: Boolean,
    roundedFull: Boolean,
    loading: Boolean,
    disabled: Boolean,
    preventDoubleClick: {
        type: Number,
        default: 0
    },
    buttonType: {
        type: String,
        default: 'button'
    },
    linkTarget: {
        type: String,
        default: '_self'
    },
    customClass: {
        type: String,
        default: ''
    },
    corners: {
        type: String,
        default: null
    },
    customSize: {
        default: {
            size: '',
            iconSize: '',
            iconSpaceLeft: '',
            iconSpaceRight: '',
            noSlot: '',
            slot: ''
        }
    },
});

const emit = defineEmits(['update:modelValue']);

const model = computed({
    get: () => props.modelValue,
    set: (val) => emit('update:modelValue', val)
});

const portalContentAndDesignStore = usePortalContentAndDesignStore();
const isEditing = ref(false);
const hoverActive = ref(false);
const attrs = useAttrs();
const inputEl = ref(null);

useEditableFocus(isEditing, inputEl)

const toggleEdit = () => {
    isEditing.value = !isEditing.value
    if (!isEditing.value) {
        hoverActive.value = false
    }
}

//Below code is copied from the btn component just to match style of the button so it's nicer ux when updating button text
const sizing = {
    huge: { size: 'text-sm', iconSize: '14', iconSpaceLeft: 'mr-2', iconSpaceRight: 'ml-2', noSlot: 'p-6', slot: 'px-6 py-4' },
    large: { size: 'text-sm', iconSize: '20', iconSpaceLeft: 'mr-2', iconSpaceRight: 'ml-2', noSlot: 'p-6', slot: 'px-6 py-3' },
    medium: { size: 'text-sm', iconSize: '20', iconSpaceLeft: 'mr-2', iconSpaceRight: 'ml-2', noSlot: 'p-2.5', slot: 'px-5 py-2.5' },
    default: { size: 'text-sm',  iconSize: '16', iconSpaceLeft: 'mr-2', iconSpaceRight: 'ml-2', noSlot: 'p-2', slot: 'px-4 py-2' },
    small: { size: 'text-xs', iconSize: '16', iconSpaceLeft: 'mr-1', iconSpaceRight: 'ml-1', noSlot: 'p-1.5', slot: 'px-3 py-1.5' },
    pill: { size: 'text-2xs', iconSize: '12', iconSpaceLeft: 'mr-0.5', iconSpaceRight: 'ml-0.5', noSlot: 'p-1.5', slot: 'px-1 py-0.5' },
    custom: {
        size: props.customSize.size,
            iconSize: props.customSize.iconSize,
            iconSpaceLeft: props.customSize.iconSpaceLeft,
            iconSpaceRight: props.customSize.iconSpaceRight,
            noSlot: props.customSize.noSlot,
            slot: props.customSize.slot
    },
}

const types = {
    primary: { style: 'bg-dark-gray hover:bg-opacity-90 border-bg-dark-gray text-white focus:ring-gray-200'},
    secondary: { style: 'bg-white hover:bg-gray-50 border-gray-300 focus:bg-gray-100' },
    ghost: { style: 'lg:bg-opacity-0 bg-opacity-10 hover:bg-gray-100 text-gray-700 shadow-none'},
    teal: { style: 'bg-teal-600 border-teal-600 text-white hover:bg-teal-500 focus:ring-gray-50' },
    white: { style: 'bg-white hover:bg-opacity-90 text-black' },
    gray: { style: 'bg-gray-600 hover:bg-gray-700 text-white focus:ring-gray-50' },
    light: { style: 'bg-gray-200 hover:bg-opacity-80 text-black text-opacity-75 shadow-none focus:ring-gray-200' },
    danger: { style: 'bg-red-400 hover:bg-opacity-90 text-white' },
    green: { style: 'bg-teal-500 hover:bg-teal-600 text-white' },
    warning: { style: 'bg-yellow-200 hover:bg-yellow-300  text-yellow-800 border-yellow-400' },
    'secondary-green': { style: 'bg-teal-50 border-gray-300 focus:bg-teal-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800 dark:focus:bg-gray-800' },
    'secondary-inverse': { style: 'bg-black bg-opacity-50 hover:bg-opacity-75 text-white' },
    'dark-green': { style: 'bg-green-600 hover:bg-opacity-90 text-white' },
    // used in both sides
    link: { style: 'border-transparent text-black hover:text-opacity-80' },


    // partner portal colors
    pink: { style: 'bg-pink-600 border-pink-600 text-white hover:bg-pink-500 focus:ring-gray-50' },
    indigo: { style: 'bg-indigo-700 border-indigo-700 text-white hover:bg-indigo-600 focus:ring-gray-50' },
    'indigo-700': { style: 'bg-indigo-700 border-indigo-700 text-white hover:bg-indigo-600 focus:ring-gray-50' },
    blue: { style: 'bg-blue-700 border-blue-700 text-white hover:bg-blue-600 focus:ring-gray-50' },
    'blue-700': { style: 'bg-blue-700 border-blue-700 text-white hover:bg-blue-600 focus:ring-gray-50' },
    cyan: { style: 'bg-cyan-700 border-cyan-700 text-white hover:bg-cyan-600 focus:ring-gray-50' },
    'cyan-700': { style: 'bg-cyan-700 border-cyan-700 text-white hover:bg-cyan-600 focus:ring-gray-50' },
    'teal-600': { style: 'bg-teal-600 border-teal-600 text-white hover:bg-teal-500 focus:ring-gray-50' },
    black: { style: 'bg-black hover:bg-opacity-90 text-white focus:ring-black-50' },
    custom: { style: 'bg-custom-btn text-white border-opacity-100 hover:bg-opacity-90 focus:ring-gray-50' },
    'custom-content': { style: 'bg-custom-content-btn text-white border-opacity-100 hover:bg-opacity-90 focus:ring-gray-50' },

}

const slots = useSlots()

const hasSlotData = computed(() => {
    return !!(model.value || slots.default?.()?.length)
})

</script>
